/**
 * FINAL-REVIEW-UX-001 (Aug 2026) — Typing Final Review workspace.
 * Near-fullscreen: PDF LEFT (large, own viewer only), Final Review RIGHT in a
 * SINGLE scrollable column (checklist in logical groups, fully visible; notes
 * prominent; confirmations; sticky footer). No nested scroll boxes.
 * Typing may correct answers/notes: corrections are APPENDED to the immutable
 * history (originals preserved + badged), the PDF regenerates and the typist
 * must re-review before Submit & Complete re-enables.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Globe, X, Loader2, CheckCircle2, ClipboardCheck, Pencil,
  StickyNote, AlertTriangle, Undo2, Save, Download,
} from 'lucide-react';
import { FINAL_REVIEW_ITEMS, CHECKLIST_GROUPS } from '../search/FinalReviewModal';
import { formatDateTimeLocal } from '../../utils/formatters';
import { DeliveryFailureDialog, parseDeliveryFailure } from './DeliveryFailureDialog';
import { PortalHandoffSection } from '../qc/PortalHandoffSection';

const API = process.env.REACT_APP_BACKEND_URL;
const ITEM_BY_KEY = Object.fromEntries(FINAL_REVIEW_ITEMS.map(i => [i.key, i]));
const fmtDT = (iso) => iso ? formatDateTimeLocal(iso) : '';
const fmtVal = (item, v) => {
  const val = (v || '').trim();
  if (!val) return '—';
  if (item?.kind === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return `${val.slice(5, 7)}/${val.slice(8, 10)}/${val.slice(0, 4)}`;
  return val;
};
const valBadgeCls = (v) => v === 'Yes' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
  : v === 'No' ? 'bg-rose-50 text-rose-700 border-rose-200'
  : v === 'NA' ? 'bg-slate-100 text-slate-600 border-slate-200'
  : 'bg-blue-50 text-blue-700 border-blue-200';
const PILL_ON = { Yes: 'bg-emerald-600 border-emerald-600 text-white', No: 'bg-rose-600 border-rose-600 text-white', NA: 'bg-slate-600 border-slate-600 text-white' };

export const FinalReportReviewModal = ({ order, prep, onClose, onCompleted, onDeliveryFailed }) => {
  const [prepData, setPrepData] = useState(prep);
  const [ack, setAck] = useState(false);
  const [verified, setVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [docUrl, setDocUrl] = useState(null);
  const [docLoading, setDocLoading] = useState(!!prep?.document);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [corrected, setCorrected] = useState(false);
  const [deliveryFailure, setDeliveryFailure] = useState(null); // TYPING-DELIVERY-VERIFY-001
  const [portalAck, setPortalAck] = useState(false); // QC-PORTAL-HANDOFF-001

  const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const doc = prepData?.document;
  const finalReview = prepData?.final_review;
  const verificationRequired = !!prepData?.checklist_verification_required;
  const portalHandoff = prepData?.is_external && prepData?.portal_handoff?.required ? prepData.portal_handoff : null;
  const corrections = finalReview?.corrections || {};
  const notesCorrection = finalReview?.notes_correction;

  useEffect(() => {
    let revoke = null;
    const load = async () => {
      if (!doc) { setDocLoading(false); return; }
      setDocLoading(true);
      try {
        const res = await axios.get(
          `${API}/api/orders/${order.id}/typing/final-report-preview`,
          { ...authHeader, responseType: 'blob', params: { token: prepData.review_token, v: doc.sha256 } });
        const url = URL.createObjectURL(res.data);
        revoke = url;
        setDocUrl(url);
      } catch {
        toast.error('Could not load the report preview');
      } finally {
        setDocLoading(false);
      }
    };
    load();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepData?.review_token, doc?.sha256]);

  const rePrepare = async () => {
    const r = await axios.post(`${API}/api/orders/${order.id}/typing/prepare-completion`, {}, authHeader);
    setPrepData(r.data);
    setAck(false);
    setVerified(false);
    setPortalAck(false);
    return r.data;
  };

  const startEdit = () => {
    setDraft({
      checklist: { ...(finalReview?.checklist || {}) },
      misc_notes: finalReview?.misc_notes || '',
    });
    setEditMode(true);
  };
  const cancelEdit = () => { setEditMode(false); setDraft(null); };
  const draftDirty = editMode && draft && (
    JSON.stringify(draft.checklist) !== JSON.stringify(finalReview?.checklist || {}) ||
    (draft.misc_notes || '').trim() !== (finalReview?.misc_notes || '').trim()
  );

  const saveCorrections = async () => {
    setSaving(true);
    try {
      const resp = await axios.post(`${API}/api/orders/${order.id}/typing/final-review-correction`,
        { checklist: draft.checklist, misc_notes: draft.misc_notes }, authHeader);
      if (resp.data?.status === 'corrected') {
        setCorrected(true);
        toast.success('Corrections saved — the PDF was regenerated. Please re-review before submitting.');
        await rePrepare();
      } else {
        toast.info('No changes to save');
      }
      setEditMode(false);
      setDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save corrections');
    } finally {
      setSaving(false);
    }
  };

  const submit = async (tokenOverride = null, isRetry = false) => {
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API}/api/orders/${order.id}/typing/complete`,
        { review_token: tokenOverride || prepData.review_token, acknowledged: true,
          checklist_verified: verificationRequired ? verified : false,
          portal_upload_confirmed: portalHandoff ? portalAck : false,
          portal_upload_file_ids: portalHandoff ? portalHandoff.documents.map(d => d.file_id) : null }, authHeader);
      const d = resp?.data?.delivery;
      if (d?.mode === 'internal_email' && d?.recipient && d?.pending_confirmation) {
        toast.warning(`Typing completed — delivery of '${d.document}' to ${d.recipient} is PENDING CONFIRMATION (email service has not answered yet). Check the order's delivery status before treating it as delivered.`, { duration: 12000 });
      } else if (d?.mode === 'internal_email' && d?.recipient) {
        toast.success(`Typing completed — '${d.document}' delivered to ${d.recipient} (email accepted)`);
      } else if (d?.mode === 'internal_email') {
        toast.success('Typing completed — no delivery email configured; delivery is pending manual dispatch');
      } else {
        toast.success('Typing completed — External Portal order (delivery via client portal)');
      }
      onCompleted?.();
      onClose?.();
    } catch (e) {
      const failure = parseDeliveryFailure(e);
      if (failure) { setDeliveryFailure(failure); return; }
      const detail = e?.response?.data?.detail;
      const detailMsg = typeof detail === 'string' ? detail : detail?.message;
      if (detail?.code === 'PORTAL_HANDOFF_REQUIRED' || detail?.code === 'PORTAL_HANDOFF_STALE') {
        toast.error(detailMsg, { duration: 8000 });
        try { await rePrepare(); } catch { /* keep modal open with current data */ }
        return;
      }
      if (e?.response?.status === 409 && !isRetry) {
        try {
          const fresh = await rePrepare();
          const sameDoc = (fresh?.document?.sha256 || null) === (doc?.sha256 || null);
          if (sameDoc) { await submit(fresh.review_token, true); return; }
          toast.info('The report changed since your preview — please review and acknowledge the latest version.');
        } catch {
          toast.error(detailMsg || 'Completion failed');
          onClose?.();
        }
      } else {
        toast.error(detailMsg || 'Completion failed');
        if (e?.response?.status === 409) onClose?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderItemRow = (key) => {
    const item = ITEM_BY_KEY[key];
    if (!item) return null;
    const finalVal = (finalReview?.checklist || {})[key] || '';
    const corr = corrections[key];
    if (editMode) {
      const dv = (draft.checklist[key] || '').trim();
      const setV = (v) => setDraft(prev => ({ ...prev, checklist: { ...prev.checklist, [key]: v } }));
      return (
        <div key={key} className="flex items-center justify-between gap-3 py-2" data-testid={`typing-checklist-item-${key}`}>
          <span className="text-[12px] text-slate-700">{item.label}</span>
          {item.kind === 'date' ? (
            <span className="flex items-center gap-1.5 shrink-0">
              <input type="date" value={dv === 'NA' ? '' : dv} disabled={dv === 'NA'}
                onChange={e => setV(e.target.value)}
                className={`h-7 text-[11.5px] border border-slate-200 rounded px-1.5 ${dv === 'NA' ? 'opacity-40' : ''}`}
                data-testid={`typing-edit-date-${key}`} />
              {item.allowNA && (
                <button type="button" onClick={() => setV(dv === 'NA' ? '' : 'NA')}
                  className={`px-2.5 py-1 text-[10.5px] font-semibold rounded-full border ${dv === 'NA' ? PILL_ON.NA : 'bg-white border-slate-200 text-slate-500'}`}
                  data-testid={`typing-edit-${key}-na`}>NA</button>
              )}
            </span>
          ) : (
            <span className="flex items-center gap-1 shrink-0">
              {item.options.map(opt => (
                <button key={opt} type="button" onClick={() => setV(opt)}
                  className={`px-2.5 py-1 text-[10.5px] font-semibold rounded-full border transition-colors ${dv === opt ? PILL_ON[opt] : 'bg-white border-slate-200 text-slate-500 hover:border-blue-300'}`}
                  data-testid={`typing-edit-${key}-${opt.toLowerCase()}`}>{opt}</button>
              ))}
            </span>
          )}
        </div>
      );
    }
    return (
      <div key={key} className="py-2" data-testid={`typing-checklist-item-${key}`}>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] text-slate-700">{item.label}</span>
          <span className={`text-[11px] font-semibold border rounded-full px-2.5 py-0.5 shrink-0 ${valBadgeCls(finalVal)}`}>{fmtVal(item, finalVal)}</span>
        </div>
        {corr && (
          <div className="mt-1 flex items-center gap-1.5 text-[10.5px]" data-testid={`typing-edited-badge-${key}`}>
            <span className="font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">Edited by Typing</span>
            <span className="text-slate-500">{corr.by} · {fmtDT(corr.at)} · was: <span className="font-semibold line-through">{fmtVal(item, corr.from) || '—'}</span></span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-[2px] flex items-center justify-center p-2 sm:p-4" data-testid="final-report-review-modal">
      <DeliveryFailureDialog failure={deliveryFailure}
        onClose={() => { setDeliveryFailure(null); onDeliveryFailed?.(); onClose?.(); }} />
      <div className="bg-white rounded-xl shadow-2xl w-[98vw] max-w-[1600px] h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2.5">
            <FileText className="h-5 w-5 text-blue-600" />
            <div>
              <span className="text-[15px] font-bold text-slate-900">Final Review — {order.order_number}</span>
              <span className="block text-[11.5px] text-slate-400">
                {prepData.is_external
                  ? 'External Portal order — delivery via client portal'
                  : <>Deliverable: <span className="font-semibold text-slate-600">{prepData.package_filename || 'Title Report PDF'}</span>{prepData.delivery_recipient ? ` → ${prepData.delivery_recipient}` : ' (no delivery email configured — delivery will be pending)'}</>}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {docUrl && doc?.filename && (
              <a
                href={docUrl}
                download={doc.filename}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                title={`Download ${doc.filename}`}
                data-testid="final-report-download-btn"
              >
                <Download className="h-3.5 w-3.5" /> Download {doc.filename}
              </a>
            )}
            <button onClick={() => !submitting && onClose?.()} className="text-slate-400 hover:text-slate-700" data-testid="close-final-review-btn">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body: PDF left, review right */}
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          {/* PDF */}
          <div className="flex-1 min-w-0 min-h-[38vh] bg-slate-200 relative" data-testid="final-review-pdf-pane">
            {docLoading ? (
              <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Generating report preview…</div>
            ) : docUrl ? (
              <iframe title="Final Search Report" src={`${docUrl}#toolbar=1&view=FitH`} className="w-full h-full border-0" data-testid="final-report-iframe" />
            ) : prepData.is_external ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 gap-2">
                <Globe className="h-8 w-8 text-slate-400" />
                <p className="text-sm font-medium">External Portal order — no internal report is generated.</p>
                <p className="text-[12px] text-slate-400">The client receives the search via their portal.</p>
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">Preview unavailable</div>
            )}
          </div>

          {/* Review column — the ONLY app scrollbar */}
          <div className="w-full lg:w-[480px] shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="final-review-right-pane">
              {/* QC-TITLE-UPLOAD-001 — the preview IS the complete package: Report → Primary → QC deliverables */}
              {!prepData.is_external && Array.isArray(doc?.package_parts) && doc.package_parts.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="final-review-package-parts">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Delivery package ({doc.package_parts.filter((p) => p.merged !== false).length} parts)</span>
                    <span className="text-[11px] text-slate-400">{doc.package_size < 1024 * 1024 ? `${Math.max(1, Math.round(doc.package_size / 1024))} KB` : `${(doc.package_size / (1024 * 1024)).toFixed(1)} MB`} · 15 MB limit</span>
                  </div>
                  <ol className="space-y-1.5">
                    {doc.package_parts.filter((p) => p.merged !== false).map((p, i) => (
                      <li key={`${p.kind}-${p.file_id || i}`} className="flex items-center gap-2 text-[12px]" data-testid={`package-part-${p.kind}`}>
                        <span className="grid place-items-center w-5 h-5 rounded-full bg-slate-100 text-[10px] font-bold text-slate-600">{i + 1}</span>
                        <span className="truncate text-slate-800 font-medium">{p.label}</span>
                        <span className={`ml-auto shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold ${p.kind === 'report' ? 'bg-blue-50 text-blue-700' : p.kind === 'primary' ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'}`}>
                          {p.kind === 'report' ? 'Final Search Report' : p.kind === 'primary' ? 'Primary Search Document' : 'QC · Final Package'}
                        </span>
                      </li>
                    ))}
                  </ol>
                  {/* FINAL-PACKAGE-001 — processor document replaced by the QC Final Package (not merged) */}
                  {doc.package_parts.filter((p) => p.kind === 'superseded_primary').map((p) => (
                    <p key={`sup-${p.file_id || 'pd'}`} className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5" data-testid="package-part-superseded-primary">
                      <span className="font-semibold">Not included:</span><span className="truncate">{p.label}</span><span className="ml-auto shrink-0 text-amber-600">Primary Search Document · replaced by QC Final Package</span>
                    </p>
                  ))}
                  <p className="mt-2 text-[10.5px] text-slate-400">The preview on the left is this exact merged PDF — the same bytes are hashed, size-checked and emailed on completion.</p>
                </div>
              )}
              {corrected && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-[11.5px] text-amber-900" data-testid="reverify-notice">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                  The PDF was regenerated with your corrections — re-review it and confirm the checkboxes again.
                </div>
              )}

              {finalReview ? (
                <div className="border border-slate-200 rounded-lg" data-testid="typing-checklist-section">
                  <div className="flex items-center gap-2 px-3.5 py-2.5 bg-slate-50 border-b border-slate-200 rounded-t-lg">
                    <ClipboardCheck className="h-4 w-4 text-slate-500" />
                    <div className="min-w-0">
                      <span className="block text-[12.5px] font-bold text-slate-800">Title Search Final Review</span>
                      <span className="block text-[10.5px] text-slate-400 truncate" data-testid="typing-checklist-meta">
                        Answered by {finalReview.original_by_name || finalReview.acknowledged_by_name}{finalReview.original_at ? ` · ${fmtDT(finalReview.original_at)}` : ''}
                      </span>
                    </div>
                    {!editMode && !order?.typing_completed_at && (
                      <button onClick={startEdit} className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800" data-testid="edit-checklist-btn">
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                    )}
                    {editMode && <span className="ml-auto text-[10.5px] font-bold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">EDITING</span>}
                  </div>
                  <div className="px-3.5 pb-1">
                    {CHECKLIST_GROUPS.map(group => (
                      <div key={group.title} className="pt-2.5">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{group.title}</div>
                        <div className="divide-y divide-slate-100">
                          {group.keys.map(renderItemRow)}
                        </div>
                      </div>
                    ))}
                    {/* REQUIRED-AMOUNT-001 — resolved from the qualifying mortgage (read-only).
                        FINAL-REVIEW-RA-001: server-resolved from the same fresh document as the PDF;
                        the orderData snapshot is only a fallback when the field is absent. */}
                    {(() => {
                      const raServer = finalReview?.required_amount;
                      const raSnap = order?.search_fulfillment?.required_amount_selection;
                      const raSel = raServer
                        ? { original_amount: raServer.amount, mortgage_number: raServer.mortgage_number }
                        : raSnap;
                      const raVal = ((raSel?.original_amount || '') + '').trim() || 'None';
                      return (
                        <div className="pt-2.5 pb-2" data-testid="typing-required-amount-row">
                          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Required Amount</div>
                          <div className="flex items-center justify-between gap-3 py-2">
                            <span className="text-[12px] text-slate-700">
                              Required Amount{raSel?.mortgage_number ? ` — from Mortgage #${raSel.mortgage_number}` : ' (no qualifying mortgage selected)'}
                            </span>
                            <span className={`text-[11px] font-semibold border rounded-full px-2.5 py-0.5 shrink-0 ${raVal !== 'None' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-testid="typing-required-amount-value">
                              {raVal}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : !prepData.is_external && (
                <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="typing-no-checklist-notice">
                  <ClipboardCheck className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-[12.5px] text-amber-900">
                    <span className="font-semibold">No checklist on file.</span> This order predates the mandatory Final Review workflow —
                    you may complete it without checklist cross-verification.
                  </p>
                </div>
              )}

              {/* Notes — prominent */}
              {finalReview && (
                <div className="border border-slate-200 rounded-lg" data-testid="typing-notes-section">
                  <div className="flex items-center gap-2 px-3.5 py-2.5 bg-slate-50 border-b border-slate-200 rounded-t-lg">
                    <StickyNote className="h-4 w-4 text-slate-500" />
                    <span className="text-[12.5px] font-bold text-slate-800">Notes / Comments</span>
                    {notesCorrection && !editMode && (
                      <span className="ml-auto text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5 whitespace-nowrap" data-testid="notes-edited-badge">
                        Edited by Typing
                      </span>
                    )}
                  </div>
                  <div className="px-3.5 py-3">
                    {editMode ? (
                      <textarea
                        rows={4}
                        className="w-full text-[12px] border border-slate-200 rounded-md px-2.5 py-2 focus:outline-none focus:border-blue-400"
                        value={draft.misc_notes}
                        onChange={e => setDraft(prev => ({ ...prev, misc_notes: e.target.value }))}
                        placeholder="Notes / additional comments included in the final PDF…"
                        data-testid="typing-notes-textarea"
                      />
                    ) : (
                      <>
                        <p className="text-[12px] text-slate-700 whitespace-pre-wrap break-words" data-testid="typing-notes-text">
                          {(finalReview.misc_notes || '').trim() || <span className="text-slate-400 italic">No notes entered.</span>}
                        </p>
                        {notesCorrection && (
                          <p className="mt-2 text-[10.5px] text-slate-400">
                            Edited by {notesCorrection.by} · {fmtDT(notesCorrection.at)} — original by {finalReview.original_by_name}: <span className="line-through">{(notesCorrection.from || '').trim() || '—'}</span>
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Confirmations */}
              {!editMode && (
                <>
                  {portalHandoff && (
                    <PortalHandoffSection dense orderId={order.id} documents={portalHandoff.documents} checked={portalAck} onChange={setPortalAck} disabled={submitting} />
                  )}
                  {verificationRequired && (
                    <label className="flex items-start gap-2.5 cursor-pointer select-none bg-blue-50/60 border border-blue-200 rounded-lg px-4 py-3">
                      <input type="checkbox" data-testid="typing-checklist-verify-checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={verified} onChange={e => setVerified(e.target.checked)} />
                      <span className="text-[12.5px] text-slate-700 font-medium">
                        I have cross-verified the Title Search Final Review checklist against the search package and supporting documents.
                      </span>
                    </label>
                  )}
                  <label className="flex items-start gap-2.5 cursor-pointer select-none bg-amber-50/60 border border-amber-200 rounded-lg px-4 py-3">
                    <input type="checkbox" data-testid="final-review-ack-checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                      checked={ack} onChange={e => setAck(e.target.checked)} />
                    <span className="text-[12.5px] text-slate-700 font-medium">
                      {prepData.is_external
                        ? 'I confirm the typed search is complete and correct for External Portal delivery.'
                        : 'I have reviewed this Final Search Report and confirm it is the correct, complete document for customer delivery.'}
                    </span>
                  </label>
                </>
              )}
            </div>

            {/* Footer — always visible */}
            <div className="border-t border-slate-200 px-4 py-3 shrink-0 bg-white">
              {editMode ? (
                <div className="flex items-center justify-between gap-2">
                  <button onClick={cancelEdit} disabled={saving}
                    className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-500 hover:text-slate-700 px-3 py-2"
                    data-testid="cancel-edit-btn">
                    <Undo2 className="h-3.5 w-3.5" /> Cancel
                  </button>
                  <button onClick={saveCorrections} disabled={saving || !draftDirty}
                    className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white text-[12.5px] font-semibold rounded-lg px-4 py-2"
                    data-testid="save-corrections-btn">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Save Corrections & Regenerate PDF
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => onClose?.()} disabled={submitting}
                    className="text-[12.5px] font-semibold text-slate-500 hover:text-slate-700 px-3 py-2"
                    data-testid="final-review-back-btn">
                    Back
                  </button>
                  <button
                    onClick={() => submit()}
                    disabled={!ack || (verificationRequired && !verified) || (!!portalHandoff && !portalAck) || submitting || docLoading}
                    className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-[13px] font-semibold rounded-lg px-5 py-2.5"
                    data-testid="final-review-submit-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Submit & Complete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalReportReviewModal;
